import { useMutation } from "@apollo/react-hooks";
import { useAsyncCallback } from 'react-use-async-callback';
import gql from "graphql-tag";
import { AsyncActionStatus } from '../../../shared/abstractStore';
import { LearningUnitUpdateInput } from '../generated/globalTypes';
import { updateLearningUnitMutation, updateLearningUnitMutationVariables } from '../generated/updateLearningUnitMutation';
import { learningUnitFieldsFragment } from '../models/LearningUnit';

/**
 * Get a callback to update a LearningUnit in the store.
 */
export function useUpdateLearningUnitCallback(): [(id: string, model: LearningUnitUpdateInput) => Promise<void>, AsyncActionStatus] {
    const [mutationAction, { error }] = useMutation<updateLearningUnitMutation, updateLearningUnitMutationVariables>(
        gql`
            mutation updateLearningUnitMutation ($model: LearningUnitUpdateInput!) {
                updateLearningUnit(model: $model) {
                    ...learningUnitFields
                }
            }

            ${learningUnitFieldsFragment}
        `
    );

    // Wrap the mutation in a standard callback.
    const [action, { isExecuting, errors: actionErrors }] = useAsyncCallback(async (id: string, model: LearningUnitUpdateInput) => {
        const result = await mutationAction({ variables: { model: { ...model, id: id } } });
        if (result.errors && result.errors.length) {
            throw result.errors;
        }
    }, [mutationAction]);

    return [action, { isExecuting: isExecuting, errors: error ? error.message : actionErrors }];
}
